import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, MessageService } from 'primeng';
import { VmeCommonModule } from '../../../../vme-common.module';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EventService, TaskService } from '../../../../services';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { JobScreenVehicleCleanlinessTask } from '../../../../interfaces/screen-specific/job-screen/common/job-screen-vehicle-cleanliness';
import { Message } from '../../../../classes/Message';
import { VehicleCleanliness } from '../../../../enums/vehicle-cleanliness.enum';

@Component({
  selector: 'job-screen-vehicle-cleanliness',
  standalone: true,
  imports: [CommonModule, ButtonModule, VmeCommonModule],
  templateUrl: './job-screen-vehicle-cleanliness.component.html',
})
export class JobScreenVehicleCleanlinessComponent implements OnInit, OnChanges {
  @Input() organisationTypeId: number;
  @Input() vehicleCleanlinessTask: JobScreenVehicleCleanlinessTask;

  editCleanlinessDialogVisible: boolean = false;

  vehicleCleanlinessSelectItems: { label: string; value: number }[] = [
    { value: 0, label: 'Not Set' },
    { value: 1, label: 'Clean' },
    { value: 2, label: 'Needs Cleaning' },
  ];

  vehicleCleanlinessForm: FormGroup;

  awaitingAllocation = false;

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    public fb: FormBuilder,
    public taskService: TaskService,
    private messageService: MessageService,
    private eventService: EventService,
  ) {
    this.vehicleCleanlinessForm = this.fb.group({
      pickupCleanliness: new FormControl(null, [Validators.required, Validators.min(0)]),
      dropoffCleanliness: new FormControl(null, [Validators.required, Validators.min(0)]),
      pickupCleanlinessComment: new FormControl(null, [Validators.maxLength(50)]),
      dropoffCleanlinessComment: new FormControl(null, [Validators.maxLength(50)]),
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.vehicleCleanlinessTask) {
      this.awaitingAllocation = this.vehicleCleanlinessTask.driverId == null;
    }
  }

  ngOnInit(): void {}

  onDialogShow() {
    this.vehicleCleanlinessForm.patchValue({
      pickupCleanliness: this.vehicleCleanlinessTask.vehicleCleanlinessDetail?.pickupCleanliness,
      dropoffCleanliness: this.vehicleCleanlinessTask.vehicleCleanlinessDetail?.dropoffCleanliness,
      pickupCleanlinessComment:
        this.vehicleCleanlinessTask.vehicleCleanlinessDetail?.pickupCleanlinessComment,
      dropoffCleanlinessComment:
        this.vehicleCleanlinessTask.vehicleCleanlinessDetail?.dropoffCleanlinessComment,
    });
    this.vehicleCleanlinessForm.markAsPristine();
  }

  saveVehicleCleanliness(): void {
    if (this.vehicleCleanlinessForm.invalid) {
      return;
    }

    const vehicleCleanliness: JobScreenVehicleCleanlinessTask = {
      id: this.vehicleCleanlinessTask.id,
      vehicleCleanlinessDetail: {
        pickupCleanliness: this.vehicleCleanlinessForm.value.pickupCleanliness,
        dropoffCleanliness: this.vehicleCleanlinessForm.value.dropoffCleanliness,
        pickupCleanlinessComment: this.vehicleCleanlinessForm.value.pickupCleanlinessComment,
        dropoffCleanlinessComment: this.vehicleCleanlinessForm.value.dropoffCleanlinessComment,
      },
    };

    this.taskService
      .saveVehicleCleanliness(vehicleCleanliness)
      .then(() => {
        this.messageService.add(
          Message.Success('Vehicle cleanliness saved', 'Update Vehicle Cleanliness'),
        );

        this.eventService.JobScreenEvent.emit({
          event: 'refresh-task-details',
        });

        this.editCleanlinessDialogVisible = false;
      })
      .catch(() => {
        this.messageService.add(
          Message.Error(
            'An error occurred while saving vehicle cleanliness',
            'Update Vehicle Cleanliness',
          ),
        );
      });
  }

  getVehicleCleanliness(vehicleCleanliness: VehicleCleanliness): string {
    const option = this.vehicleCleanlinessSelectItems.find(
      (vc) => vc.value == vehicleCleanliness?.valueOf() || 0,
    );
    return option?.label ?? '';
  }

  protected showEditCleanlinessDialog() {
    this.editCleanlinessDialogVisible = true;
  }
}
